import { FC, useEffect, useState } from 'react'
import { createSearchParams, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { PageLoader } from 'components/PageLoader'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { CreateDentalSchoolInput } from '__generatedTypes__/globalTypes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { findOneDentalSchool, findOneDentalSchoolVariables } from '__generatedTypes__/findOneDentalSchool'
import { updateDentalSchool, updateDentalSchoolVariables } from '__generatedTypes__/updateDentalSchool'
import { removeDentalSchool, removeDentalSchoolVariables } from '__generatedTypes__/removeDentalSchool'
import { UPDATE_DENTAL_SCHOOL, FIND_ONE_DENTAL_SCHOOL, REMOVE_DENTAL_SCHOOL } from 'queries/dentalSchools'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { QUERY_PARAM, SCHOOL_STATISTICS_TAB } from 'pagesNew/AdmissionManagement'
import { toast } from 'utils/toast'
import { FormValues, Form, getInitialValues } from '../Form'
import { handleServerErrors } from '../Create'

type Props = {
  platform: Platform
  isOptometry?: boolean
}

export const Edit: FC<Omit<Props, 'platform'>> = withPlatformCheck(({ platform, isOptometry }: Props) => {
  const { dentalSchoolId } = useParams()
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()
  const sectionId = searchParams.get('sectionId')
  const [initialValues, setInitialValues] = useState<FormValues | null>(null)
  const { data, loading } = useQuery<findOneDentalSchool, findOneDentalSchoolVariables>(FIND_ONE_DENTAL_SCHOOL, {
    variables: {
      id: String(dentalSchoolId),
    },
    skip: !dentalSchoolId,
    fetchPolicy: 'network-only',
  })
  const [updateDentalSchool] = useMutation<updateDentalSchool, updateDentalSchoolVariables>(UPDATE_DENTAL_SCHOOL)
  const [removeDentalSchool] = useMutation<removeDentalSchool, removeDentalSchoolVariables>(REMOVE_DENTAL_SCHOOL)

  useEffect(() => {
    if (!data?.findOneDentalSchool) {
      return
    }

    ;(async () => {
      const initialValues = await getInitialValues(platform.id, data.findOneDentalSchool)
      setInitialValues(initialValues)
    })()
  }, [platform.id, data?.findOneDentalSchool])

  const redirectBack = () => {
    if (sectionId) {
      navigate(
        isOptometry
          ? cmsRoutes.ORGANIZE_EDIT_OPTOMETRY_SCHOOL_STATISTICS
          : cmsRoutes.ORGANIZE_EDIT_DENTAL_SCHOOL_STATISTICS,
        {
          id: sectionId,
        }
      )

      return
    }

    navigate(
      isOptometry ? cmsRoutes.OPTOMETRY : cmsRoutes.DENTISTRY,
      {},
      { searchParams: createSearchParams({ [QUERY_PARAM]: SCHOOL_STATISTICS_TAB }) }
    )
  }

  const handleSubmit = async (values: CreateDentalSchoolInput) => {
    try {
      await updateDentalSchool({
        variables: {
          updateDentalSchoolInput: {
            ...values,
            id: String(dentalSchoolId),
          },
        },
      })
      redirectBack()
      toast.success(`${isOptometry ? 'Optometry' : 'Dental'} school updated successfully`)
    } catch (error) {
      toast.error(`Failed to update ${isOptometry ? 'optometry' : 'dental'} school`)

      return handleServerErrors(error)
    }
  }

  const handleRemove = async () => {
    try {
      await removeDentalSchool({
        variables: {
          removeDentalSchoolId: String(dentalSchoolId),
        },
      })
      redirectBack()
      toast.success(`${isOptometry ? 'Optometry' : 'Dental'} school removed successfully`)
    } catch (error) {
      toast.error(`Failed to remove ${isOptometry ? 'optometry' : 'dental'} school`)
    }
  }

  if (!loading && !data?.findOneDentalSchool) {
    return <Typography variant="title1">{isOptometry ? 'Optometry' : 'Dental'} School not found</Typography>
  }

  if (!initialValues) {
    return <PageLoader />
  }

  return (
    <Form
      initialValues={initialValues}
      isOptometry={!!initialValues.isOptometry}
      onSubmit={handleSubmit}
      isEdit
      onDelete={handleRemove}
    />
  )
})
